import React, { useState } from "react";
import {
  FiUser as RawFiUser,
  FiLock as RawFiLock,
  FiEye as RawFiEye,
  FiEyeOff as RawFiEyeOff,
} from "react-icons/fi";
import { useMsal } from "@azure/msal-react";
import * as S from "./LoginPage.styles";
import { useNavigate } from "react-router-dom";
import { loginNomina, login365 } from "../../api/api";
import notify from "devextreme/ui/notify";
import { apiService } from "../../services/apiService";
import logoImg from "../../assets/img/infra-logo2.svg";

const FiUser = RawFiUser as unknown as React.FC<{ size?: number }>;
const FiLock = RawFiLock as unknown as React.FC<{ size?: number }>;
const FiEye = RawFiEye as unknown as React.FC<{ size?: number }>;
const FiEyeOff = RawFiEyeOff as unknown as React.FC<{ size?: number }>;

const Login: React.FC = () => {
  const [user, setUser] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const [loginAttempts, setLoginAttempts] = useState<number>(0);
  const [isLockoutModalOpen, setIsLockoutModalOpen] = useState<boolean>(false);

  const { instance } = useMsal();
  const navigate = useNavigate();

  const handleAzureLogin = () => {
    instance
      .loginPopup({
        scopes: ["user.read"],
      })
      .then(async (response) => {
        const values = {
          correo: response.account.username,
          nombre: response.account.name,
        };

        if (values.correo && values.nombre) {
          const response = await login365(values.correo);
          if (response.status === 1) {
            notify("Inicio de sesión éxito.", "success", 2000);
            navigate("/inicio");
          } else if (response.status === 2) {
            notify(`${response.data}`, "danger", 2000);
          }
        }
      })
      .catch((error: any) => {
        console.log("error login", error);
      });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (loginAttempts >= 5) {
      setIsLockoutModalOpen(true);
      return;
    }

    const handleFailedAttempt = () => {
      const newAttemptCount = loginAttempts + 1;
      setLoginAttempts(newAttemptCount);

      if (newAttemptCount >= 5) {
        setIsLockoutModalOpen(true);
      } else {
        const attemptsLeft = 5 - newAttemptCount;
        notify(
          `Credenciales incorrectas o error. Quedan ${attemptsLeft} ${
            attemptsLeft === 1 ? "intento" : "intentos"
          }.`,
          "warning",
          3500,
        );
      }
    };

    try {
      const response = await loginNomina(user, password);

      if (response && response.status === 1) {
        notify("Inicio de sesión éxito.", "success", 2000);
        setLoginAttempts(0);
        navigate("/inicio");
      } else {
        handleFailedAttempt();
      }
    } catch (error) {
      console.error("Error en la llamada de login:", error);
      handleFailedAttempt();
    }
  };

  const handleAcceptLockout = async () => {
    setIsLockoutModalOpen(false);

    try {
      await apiService.patch(`/api/Usuarios/cambiastatus/${user}`, "");
      notify(
        "Tu cuenta ha sido bloqueada. Por favor comunícate con tu administrador.",
        "error",
        4000,
      );
    } catch (error) {
      console.error("Error al intentar bloquear el usuario:", error);
      notify(
        "No se pudo completar la acción. Contacta al administrador.",
        "error",
        4000,
      );
    }
  };

  const isSubmitDisabled = !user || !password || loginAttempts >= 5;

  return (
    <S.PageContainer>
      <S.BrandingPanel>
        <S.InfraLogo src={logoImg} alt="Logo INFRA" />
        <S.Slogan>
          Garantizamos el suministro de nuestros gases en todo México
        </S.Slogan>
      </S.BrandingPanel>

      <S.FormPanel>
        <S.FormContainer onSubmit={handleSubmit}>
          <S.Title>Bienvenido etiquetado de envases</S.Title>
          <S.Subtitle>
            Ingresa tus credenciales para acceder al portal.
          </S.Subtitle>

          <S.InputGroup>
            <S.InputIcon>
              <FiUser size={20} />
            </S.InputIcon>
            <S.Input
              type="text"
              placeholder="Nómina"
              value={user}
              onChange={(e) => setUser(e.target.value)}
              required
              maxLength={6}
            />
          </S.InputGroup>

          <S.InputGroup>
            <S.InputIcon>
              <FiLock size={20} />
            </S.InputIcon>
            <S.Input
              type={showPassword ? "text" : "password"}
              placeholder="Contraseña"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <S.PasswordToggleIcon
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
            </S.PasswordToggleIcon>
          </S.InputGroup>

          <S.SubmitButton type="submit" disabled={isSubmitDisabled}>
            {loginAttempts >= 5 ? "Cuenta Bloqueada" : "Iniciar Sesión"}
          </S.SubmitButton>

          <S.SecondaryButton type="button" onClick={handleAzureLogin}>
            Iniciar sesión con Microsoft 365
          </S.SecondaryButton>
        </S.FormContainer>
      </S.FormPanel>

      {isLockoutModalOpen && (
        <S.ModalOverlay>
          <S.ModalContent>
            <S.ModalHeader>
              <h3>Cuenta Bloqueada</h3>
            </S.ModalHeader>

            <p
              style={{
                textAlign: "center",
                fontSize: "1rem",
                lineHeight: "1.5",
                color: "#333",
              }}
            >
              Tu cuenta ha sido bloqueada por límite de intentos de inicio de
              sesión.
              <br />
              Por favor comunícate con tu administrador.
            </p>

            <S.ModalActions>
              <S.SubmitButton
                type="button"
                onClick={handleAcceptLockout}
                style={{ width: "100%" }}
              >
                Aceptar
              </S.SubmitButton>
            </S.ModalActions>
          </S.ModalContent>
        </S.ModalOverlay>
      )}
    </S.PageContainer>
  );
};

export default Login;
